import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useProperty } from "@/context/PropertyContext";
import { Loader2, Wrench, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const CAT_COLORS = {
  Plumbing: "bg-sky-500",
  Electrical: "bg-amber-500",
  Carpentry: "bg-orange-500",
  Civil: "bg-stone-500",
  Painting: "bg-violet-500",
  "Swimming Pool": "bg-cyan-500",
  "Water Fountains": "bg-teal-500",
  Other: "bg-zinc-400",
};

/**
 * Active maintenance ticket load grouped by official category. Shows a
 * horizontal bar per category proportional to its share of the open load,
 * with a high-priority sub-count. Property-scoped via PropertyContext.
 */
export default function MaintenanceLoadByCategory() {
  const { selectedId } = useProperty();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const url = selectedId
      ? `/maintenance/load-by-category?property_id=${selectedId}`
      : "/maintenance/load-by-category";
    api.get(url)
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedId]);

  const max = data ? Math.max(1, ...data.rows.map((r) => r.count)) : 1;

  return (
    <section className="bg-white border border-zinc-200 rounded-md p-4 sm:p-5" data-testid="maintenance-load-widget">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div>
          <span className="label-overline">Maintenance</span>
          <h3 className="font-display text-base sm:text-lg font-bold tracking-tight flex items-center gap-2">
            <Wrench className="w-4 h-4 text-zinc-500" /> Open load by category
          </h3>
        </div>
        <div className="flex items-center gap-3">
          {data && <span className="text-xs text-zinc-400">{data.total} open ticket{data.total === 1 ? "" : "s"}</span>}
          <Link to="/tickets" className="text-xs text-zinc-600 hover:text-zinc-900 flex items-center gap-1" data-testid="maintenance-load-see-tickets">
            View <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : !data ? (
        <p className="text-sm text-zinc-400 py-4">Couldn't load maintenance data.</p>
      ) : data.total === 0 ? (
        <p className="text-sm text-zinc-400 py-6 text-center" data-testid="maintenance-load-empty">No open maintenance tickets. All clear.</p>
      ) : (
        <div className="space-y-2.5">
          {data.rows.filter((r) => r.count > 0).map((r) => (
            <div key={r.category} className="flex items-center gap-3" data-testid={`maintenance-cat-${r.category.replace(/\s+/g, "-")}`}>
              <div className="w-28 sm:w-36 text-xs sm:text-sm text-zinc-700 truncate shrink-0">{r.category}</div>
              <div className="flex-1 h-6 bg-zinc-100 rounded overflow-hidden">
                <div
                  className={`h-full ${CAT_COLORS[r.category] || "bg-zinc-400"} transition-all`}
                  style={{ width: `${Math.round((r.count / max) * 100)}%` }}
                />
              </div>
              <div className="w-20 text-right text-sm shrink-0">
                <span className="font-mono font-semibold text-zinc-900">{r.count}</span>
                {r.high_priority > 0 && (
                  <span className="ml-1 text-[10px] font-semibold text-red-600">· {r.high_priority} hi</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
